// @ts-strict-ignore
import { withEmotionCache, type EmotionCache } from "@emotion/react";
import { useRouteError } from "@remix-run/react";
import { captureRemixErrorBoundaryError } from "@sentry/remix";
import { useEffect } from "react";
import { I18nextProvider } from "react-i18next";

import { AviaryProvider } from "@aviary/utils";
import { SAFE_LOADER_ERROR_CODES } from "@remix/helpers/loaders/useSafeLoaderData";
import { i18n } from "@remix/locales/i18n";
import { ErrorFallback } from "@shared/components/ErrorFallback/ErrorFallback";

import { Document } from "../Document/Document";

import { useResetEmotionStyles } from "./useResetEmotionStyles/useResetEmotionStyles";

const isDataLoaderMismatchError = (error: any) => {
  return (
    error &&
    typeof error === "object" &&
    error.status === SAFE_LOADER_ERROR_CODES.conflict &&
    error.code === "DATA_LOADER_MISMATCH"
  );
};

const ErrorBoundary = (_, emotionCache: EmotionCache) => {
  const error = useRouteError();
  useResetEmotionStyles({ emotionCache });

  useEffect(() => {
    i18n.changeLanguage("en");
  }, []);

  captureRemixErrorBoundaryError(error);

  // Return null on 409 conflict error from useSafeLoaderData to show blank page while it refreshes
  if (isDataLoaderMismatchError(error)) {
    return null;
  }

  return (
    <Document>
      <I18nextProvider i18n={i18n}>
        <AviaryProvider isDS3>
          <ErrorFallback hideBackToHome />
        </AviaryProvider>
      </I18nextProvider>
    </Document>
  );
};

const ErrorBoundaryWithEmotionCache = withEmotionCache(ErrorBoundary);

export { ErrorBoundaryWithEmotionCache as ErrorBoundary };
